export type FederatedProvider =
  | 'google'
  | 'google_workspace'
  | 'yahoo'
  | 'microsoft'
  | 'apple';

export const FEDERATED_PROVIDERS: FederatedProvider[] = [
  'google',
  'google_workspace',
  'yahoo',
  'microsoft',
  'apple',
];

export const isFederatedProvider = (
  value: unknown
): value is FederatedProvider =>
  typeof value === 'string' &&
  (FEDERATED_PROVIDERS as string[]).includes(value);

export interface FederatedStartOptions {
  /**
   * Relative path the backend should return the user to after the OAuth
   * round-trip. Sent as `next`; the backend echoes it back to
   * `/auth/federated/complete` and the completion page validates it before
   * navigating.
   */
  next?: string;
  /**
   * When true, sends `accept_tos=1` so the backend records the user's
   * agreement to the Terms of Service as part of the federated sign-in.
   */
  acceptTos?: boolean;
}

// Mirrors the base used by src/utils/axios.tsx so the browser is sent to the
// same API origin. VITE_API_ROOT is absolute in development and relative
// (`/api/`) in production.
const DEFAULT_API_ROOT = 'http://localhost:8000/api/';

const getApiRoot = (): string =>
  import.meta.env.VITE_API_ROOT || DEFAULT_API_ROOT;

/**
 * Builds the backend start URL for the OAuth authorization-code flow. `email`
 * is optional and lets the backend pick the tenant (staff Workspace vs.
 * external) by domain. `next` and `acceptTos` are forwarded to the backend
 * when provided so the round-trip can resume the caller's flow and record
 * terms acceptance.
 */
export const buildFederatedStartUrl = (
  provider: FederatedProvider,
  email?: string,
  options: FederatedStartOptions = {}
): string => {
  const root = getApiRoot();
  const base = root.endsWith('/') ? root : `${root}/`;
  const url = `${base}auth/federated/start/`;

  const params = new URLSearchParams();
  params.set('provider', provider);
  if (email) {
    params.set('email', email);
  }
  if (options.next) {
    params.set('next', options.next);
  }
  if (options.acceptTos) {
    params.set('accept_tos', '1');
  }

  const query = params.toString();
  return query ? `${url}?${query}` : url;
};

/**
 * Starts federated sign-in by navigating the browser to the backend start URL.
 * Kept separate from the button so the redirect side effect is mockable in
 * tests (jsdom's `window.location` is read-only).
 */
export const startFederatedSignIn = (
  provider: FederatedProvider,
  email?: string,
  options: FederatedStartOptions = {}
): void => {
  window.location.assign(buildFederatedStartUrl(provider, email, options));
};
